import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import emailjs from '@emailjs/browser';

// Constants
const IMAGE_SETS = {
  floorPlans: [
    { src: "/image/fp1.png", description: "Iconic Cluster" },
    { src: "/image/fp2.png", description: "4BHK+SER.ROOM - UNIT PLAN (3769 SQ.FT.)" },
    { src: "/image/fp3.png", description: "5BHK+SER.ROOM - DUPLEX UNIT PLAN (7262 SQ.FT.)" },
    { src: "/image/fp4.png", description: "Iconic Cluster Type 1/2" },
    { src: "/image/fp5.png", description: "4BHK+SER.ROOM (2783 SQ.FT.)" },
    { src: "/image/fp6.png", description: "3BHK+SER.ROOM- UNIT PLAN (2299 SQ.FT.)" },
  ],

  masterPlans: [
    "/image/master1.png",
    "/image/loc.png",
  ],
  gallery: [
    '/image/gallery1.png',
    '/image/gallery2.png',
    '/image/gallery3.jpg',
    '/image/gallery4.jpg',
    '/image/gallery5.jpg',
    '/image/gallery6.jpg',
    '/image/gallery7.jpg',
    '/image/gallery8.jpg',
    '/image/gallery9.jpg',
    '/image/main.png',

  ],
  projectDetails: {
    standardTower: ["/image/gallery1.png"],
    iconicTower: ["/image/ps1.jpg"]
  }
};

const PDF_TYPES = {
  brochure: {
    title: "Project Brochure",
    desc: "Detailed project information",
    icon: "📄"
  },
  specifications: {
    title: "Price List",
    desc: "Floor wise price details",
    icon: "💰"
  }
};

const AMENITIES = [
  { name: "Swimming Pool", icon: "🏊", image: "/image/pool.jpg" },
  { name: "Fitness Center", icon: "💪", image: "/image/gym.jpg" },
  { name: "Landscaped Gardens", icon: "🌳", image: "/image/park.jpg" },
  { name: "Clubhouse", icon: "🏛️", image: "/image/clubhouse.jpg" },
  { name: "Badminton", icon: "🎾", image: "/image/bdmntn.jpg" },
  { name: "Kids Play Zone", icon: "🧒", image: "/image/playarea.jpg" },
  { name: "Open Gym", icon: "🏃", image: "/image/opengym.jpg" },
  { name: "Party Hall", icon: "🎉", image: "/image/partyhall.jpg" }
];

const PRICE_LIST = [
  { type: "Standard Tower", launchPrice: "13,000/-", offerPrice: "9,750/-*" },
  { type: "Iconic Tower", launchPrice: "14,500/-", offerPrice: "10,875/-*" }
];

// Reusable Components
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="modal" onClick={onClose}>
      {children}
    </div>
  );
};

const ContactForm = ({ onSubmit, title = "Let's Connect", buttonText = "Submit" }) => (
  <form className="contact-form" onSubmit={onSubmit}>
    <h3 style={{ color: '#5D4037', marginBottom: '1em', textAlign: 'center' }}>{title}</h3>
    <input type="text" name="name" placeholder="Your Name" required />
    <input type="email" name="email" placeholder="Email Address" required />
    <input type="tel" name="phone" placeholder="Phone Number" required />
    <textarea name="message" placeholder="Your Message" rows="4" required></textarea>
    <button type="submit">{buttonText}</button>
  </form>
);

const AmenityCard = ({ name, icon, image }) => (
  <div className="amenity-card">
    <img src={image} alt={name} className="amenity-bg" loading="lazy" />
    <div className="amenity-content">
      <div className="amenity-icon">{icon}</div>
      <h3 className="amenity-title">{name}</h3>
    </div>
  </div>
);

const PdfCard = ({ type, onClick }) => (
  <div className="pdf-card" onClick={() => onClick(type)}>
    <div className="pdf-icon">{PDF_TYPES[type].icon}</div>
    <h3 className="pdf-title">{PDF_TYPES[type].title}</h3>
    <p className="pdf-desc">{PDF_TYPES[type].desc}</p>
  </div>
);

function App() {
  // State management
  const [modalState, setModalState] = useState({
    open: false,
    images: [],
    currentIndex: 0
  });



  const [contactPopup, setContactPopup] = useState({
    show: false,
    card: null
  });

  const [pdfModal, setPdfModal] = useState({
    show: false,
    type: null,
    submitted: false
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // EmailJS initialization
  useEffect(() => {
    emailjs.init('bpo1kkRrNW-gZdk3X');
  }, []);

  // Scroll handling
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll("section[id]");
      const navLinks = document.querySelectorAll(".nav-links a");
      let current = "";

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 100) {
          current = section.getAttribute("id");
        }
      });

      navLinks.forEach((link) => {
        link.classList.remove("active");
        if (link.getAttribute("href") === `#${current}`) {
          link.classList.add("active");
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  // Intersection observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const fadeUpElements = document.querySelectorAll(".fade-up");
    fadeUpElements.forEach((el) => observer.observe(el));
  }, []);

  // Navigation handlers
  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    const target = document.getElementById(sectionId);
    const yOffset = -80;
    const yPosition = target.getBoundingClientRect().top + window.scrollY + yOffset;

    window.scrollTo({ top: yPosition, behavior: "smooth" });
    setIsMenuOpen(false);
  };

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMenuOpen && !e.target.closest('.nav-links') && !e.target.closest('.hamburger')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  // Image modal handlers
  const openModal = useCallback((images, index) => {
    if (!contactPopup.show) {
      setContactPopup({ show: true, card: { images, index } });
    } else {
      setModalState({ open: true, images, currentIndex: index });
    }
  }, [contactPopup.show]);

  const closeModal = useCallback(() => {
    setModalState(prev => ({ ...prev, open: false }));
  }, []);

  const navigateImage = useCallback((direction) => {
    setModalState(prev => {
      const newIndex = direction === 'next'
        ? (prev.currentIndex + 1) % prev.images.length
        : (prev.currentIndex - 1 + prev.images.length) % prev.images.length;
      return { ...prev, currentIndex: newIndex };
    });
  }, []);




  // Form submission handlers
  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();

    try {
      const response = await emailjs.sendForm(
        'service_vs5x1pn',
        'template_0t7270k',
        e.target,
        'U7cKhUJ8hwRNsrvJQ'
      );

      if (response.status === 200) {
        alert("Thank you! Your message has been sent successfully.");
        e.target.reset();
      }
    } catch (error) {
      console.error("Failed to send email:", error);
      alert("Oops! Something went wrong. Please try again later.");

      // Fallback to mailto
      const formData = new FormData(e.target);
      const name = formData.get("name");
      const email = formData.get("email");
      const phone = formData.get("phone");
      const message = formData.get("message");

      const mailtoLink = document.createElement("a");
      mailtoLink.href = `mailto:vikas.sharma.nsn@gmail.com?subject=New Inquiry from ${name}&body=${encodeURIComponent(
        `Name: ${name}\nEmail: ${email}\nPhone: ${phone}\nMessage: ${message}`
      )}`;
      mailtoLink.click();
    }
  }, []);

  const handleEnquirySubmit = useCallback(async (e) => {
    e.preventDefault();

    try {
      const response = await emailjs.sendForm(
        'service_vs5x1pn',
        'template_0t7270k',
        e.target,
        'U7cKhUJ8hwRNsrvJQ'
      );

      if (response.status === 200) {
        alert("Thank you! Your enquiry has been submitted.");
        setContactPopup({ show: false, card: null });

        if (contactPopup.card) {
          setModalState({
            open: true,
            images: contactPopup.card.images,
            currentIndex: contactPopup.card.index
          });
        }
        e.target.reset();
      }
    } catch (error) {
      console.error("Failed to send enquiry:", error);
      alert("There was an error submitting your enquiry. Please try again.");
    }
  }, [contactPopup.card]);

  const handlePdfRequest = useCallback((type) => {
    setPdfModal({ show: true, type, submitted: false });
  }, []);

  const handlePdfDownload = useCallback(() => {
    const pdfUrl = pdfModal.type === 'brochure'
      ? '/pdf/project-brochure.pdf'
      : '/pdf/price-list.pdf';

    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = true;
    link.click();

    setPdfModal({ show: false, type: null, submitted: false });
  }, [pdfModal.type]);

  return (
    <div className="App">
      <nav className="navbar">
        <div className="logo">
          <img src="/image/logo.png" alt="3 BHK Flat at Gaurs Leisure Park Greater Noida West with Modern Interiors" />
          <img src="/image/logo2.png" alt="3 BHK Flat at Gaurs Leisure Park Greater Noida West with Modern Interiors" />
        </div>

        <div
          className={`nav-links ${isMenuOpen ? 'open' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <a href="#home" onClick={(e) => handleNavClick(e, "home")}>Home</a>
          <a href="#overview" onClick={(e) => handleNavClick(e, "overview")}>Overview</a>
          <a href="#amenities" onClick={(e) => handleNavClick(e, "amenities")}>Amenities</a>
          <a href="#floor-plan" onClick={(e) => handleNavClick(e, "floor-plan")}>Floor Plan</a>
          <a href="#project-layout" onClick={(e) => handleNavClick(e, "project-layout")}>Project Layout</a>
          <a href="#gallery" onClick={(e) => handleNavClick(e, "gallery")}>Gallery</a>
          <a href="#price-list" onClick={(e) => handleNavClick(e, "price-list")}>Price List</a>
          <a href="#master-plan" onClick={(e) => handleNavClick(e, "master-plan")}>Location</a>
          <a href="#contact" onClick={(e) => handleNavClick(e, "contact")}>Contact Us</a>

        </div>

        <div
          className={`hamburger ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
        >
          <div></div>
          <div></div>
          <div></div>
        </div>
      </nav>

      <div id="home" className="home-section fade-up">
        <img src="/image/main.png" alt="3 BHK Flat at Gaur Leisure Park Greater Noida West with Modern Interiors" />
        <div className="home-form-container fade-up">
          <ContactForm onSubmit={handleFormSubmit} />
        </div>
      </div>

      <section id="overview" className="fade-up">
        <div className="overview-container">
          <div className="overview-content">
            <h2>Overview</h2>

            <h3 style={{ color: '#5D4037', marginTop: '1.5em' }}>
              Aspire Leisure Park by Gaurs – Premium Residences in Greater Noida West
            </h3>

            <p style={{ marginTop: '1em', lineHeight: '1.8' }}>
              Aspire Leisure Park by Gaurs is a luxury residential development located in the prime Techzone 4 area of Greater Noida West (Noida Extension), offering an unmatched blend of space, elegance, and modern comfort.
            </p>

            <p style={{ marginTop: '1em', lineHeight: '1.8' }}>
              Designed for discerning families, the project features expansive 3 BHK + Servant, 4 BHK + Servant, and 5 BHK + Servant apartments, ranging from 2,299 sq. ft. to 7,262 sq. ft., setting new standards for spacious living in the region.
            </p>

            <p style={{ marginTop: '1em', lineHeight: '1.8' }}>
              Every home is meticulously planned with smart layouts, ample natural light, and premium interiors to deliver a refined lifestyle.
            </p>
          </div>

          <div className="overview-image"></div>
        </div>
      </section>

      <section id="amenities" className="fade-up">
        <div className="amenities-container">
          <h2 style={{ color: '#5D4037' }}>Amenities</h2>
          <p style={{ textAlign: 'center', color: '#8D6E63', marginBottom: '2em' }}>
            Premium facilities for modern living
          </p>

          <div className="amenities-grid">
            {AMENITIES.map((amenity, index) => (
              <AmenityCard
                key={index}
                name={amenity.name}
                icon={amenity.icon}
                image={amenity.image}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="floor-plan" className="floor-section fade-up">
        <h2>Floor Plans</h2>
        <div className="thumbnail-row">
          {IMAGE_SETS.floorPlans.map((plan, i) => (
            <div
              className="image-container"
              key={i}
              onClick={() => {
                setContactPopup({
                  show: true,
                  card: { images: IMAGE_SETS.floorPlans, index: i }
                });
              }}
            >
              <img src={plan.src} alt={`Floor ${i + 1}`} className="blurred-image" />
              <div className="enquire-overlay">
                <h4>{plan.description}</h4>
                <button className="enquire-btn">Enquire Now</button>
              </div>

            </div>
          ))}

        </div>
      </section>

      <section id="project-layout" className="fade-up">
        <h2>Project Layout</h2>
        <div className="project-cards">
          <div className="card">
            <img src={IMAGE_SETS.projectDetails.standardTower[0]} alt="3 BHK Flat at Gaur Leisure Park Greater Noida West with Modern Interiors" className="card-img" />
            <div className="card-overlay">
              <h3>Standard Towers</h3>
              <ul>
                <li><strong>4BHK + Servant Room:</strong> Carpet: 1,709 sq.ft | Super: 2,783 sq.ft</li>
                <li><strong>3BHK + Study + Servant:</strong> Carpet: 1,354 sq.ft | Super: 2,304 sq.ft</li>
              </ul>
              <button
                className="enquire-btn"
                onClick={() => {
                  setContactPopup({
                    show: true,
                    card: {
                      images: IMAGE_SETS.projectDetails.standardTower,
                      index: 0
                    }
                  });
                }}
              >
                Enquire Now
              </button>
            </div>
          </div>

          <div className="card">
            <img src={IMAGE_SETS.projectDetails.iconicTower[0]} alt="3 BHK Flat at Gaurs Leisure Park Greater Noida West with Modern Interiors Iconic Tower" className="card-img" />
            <div className="card-overlay">
              <h3>Iconic Tower</h3>
              <ul>
                <li><strong>4BHK + Servant Room:</strong> Carpet: 1,915 sq.ft | Super: 3,769 sq.ft</li>
                <li><strong>5BHK Duplex + Servant:</strong> Carpet: 3,088 sq.ft | Super: 7,262 sq.ft</li>
              </ul>
              <button
                className="enquire-btn"
                onClick={() => {
                  setContactPopup({
                    show: true,
                    card: {
                      images: IMAGE_SETS.projectDetails.iconicTower,
                      index: 0
                    }
                  });
                }}
              >
                Enquire Now
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="gallery" style={{ padding: '3em 1em', background: '#f9f5f0' }} className="fade-up">
        <h2 style={{ color: '#5D4037', textAlign: 'center', marginBottom: '0.5em' }}>Society Gallery</h2>
        <p style={{ color: '#8D6E63', textAlign: 'center', marginBottom: '2em' }}>
          Explore our vibrant community
        </p>

        <div className="gallery-grid">
          {IMAGE_SETS.gallery.map((img, index) => (
            <div key={index} className="gallery-item">
              <img
                src={img}
                alt={`Society view ${index + 1}`}
                loading="lazy"
                onClick={() => setSelectedImage(img)}
                style={{ cursor: 'pointer' }} />
            </div>
          ))}
        </div>
      </section>

      <section id="price-list" className="price-section fade-up">
        <h2>Price List</h2>
        <div className="price-table-container">
          <table className="price-table">
            <thead>
              <tr>
                <th>Tower Type</th>
                <th>Launch Price (₹ / Sq.Ft.)</th>
                <th>Inaugural Offer (₹ / Sq.Ft.)</th>
              </tr>
            </thead>
            <tbody>
              {PRICE_LIST.map((item, index) => (
                <tr key={index}>
                  <td>{item.type}</td>
                  <td>{item.launchPrice}</td>
                  <td>{item.offerPrice}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="additional-charges">
          <p>* Plus applicable Floor/View PLCs, Car Parking, Club Membership, Electricity & Backup, Lease Rent & IFMS</p>
        </div>
        <div >
          <button className="btn" onClick={() => setContactPopup({ show: true })}>Book Now</button>
        </div>
      </section>

      <section id="master-plan" className="fade-up">
        <h2>Location & Master Plan</h2>
        <div className="master-img">
          {IMAGE_SETS.masterPlans.map((src, i) => (
            <img
              src={src}
              key={i}
              alt={`Master ${i + 1}`}
              onClick={() => setSelectedImage(src)}
            />
          ))}
        </div>
      </section>

      <section className="pdf-download-section fade-up">
        <h2 style={{ color: '#5D4037' }}>Project Documents</h2>
        <p style={{ color: '#8D6E63' }}>Download detailed project information</p>

        <div className="pdf-container">
          {Object.keys(PDF_TYPES).map(type => (
            <PdfCard
              key={type}
              type={type}
              onClick={handlePdfRequest}
            />
          ))}
        </div>
      </section>

      <section className="fade-up">
        <div className="details">
          <h1 style={{ color: '#5D4037' }}>Gaur Groups</h1>
          <br />
          <p>For the past 25 years, Gaursons India has been a leading name in the real estate sector of the National Capital Region. With a legacy of excellence and over 45 successful projects, the company has consistently delivered outstanding results, even in challenging times. As a customer-centric organization, Gaursons India is committed to serving both homebuyers and investors with integrity and dedication. Gaurs Group, recognized as the best real estate company in Greater Noida West, is known for its superior construction quality, unwavering commitment, and innovative building technology. As a top real estate developer in Noida, the company boasts a team of highly skilled professionals and expert management. Gaursons India is actively shaping various sectors, including residential, commercial, retail, hospitality, healthcare, and education, making a significant impact across multiple industries</p>
        </div>
      </section>

      <section id="contact" className="fade-up">
        <h2>Contact Us</h2>
        <p><strong>Phone:</strong> +91-9211310599</p>
        <p><strong>Phone:</strong> +91-9315979953</p>
        <br />
      </section>

      <footer className="footer">
        <div className="footer-content">
          <h5>Copyright © 2025 <strong>Gaur Aspire Leisure Park</strong></h5>
          <br />
          <p>Disclaimer:- The content on this website is for informational purposes only and does not constitute an offer to avail any services. Prices are subject to change without prior notice, and the availability of properties is not guaranteed. Images displayed are for representation purposes only and may not accurately reflect the actual properties. Please note that this is the official website of an authorized marketing partner. We may share data with RERA-registered brokers or companies for processing as required. Additionally, we may send updates and information to the mobile number or email address you provide.</p>
          <br />
          <p>All rights are reserved. The content, design, and information on this website are protected by copyright and other intellectual property rights. Unauthorized use or reproduction may violate applicable laws.</p>
          <br />
          <p>For accurate and up-to-date information regarding services, pricing, availability, and other details, please contact us directly using the contact information provided on this website.</p>
          <br />
          <h4><strong>Thank you for visiting our website.</strong></h4>
        </div>
      </footer>

      <Modal isOpen={modalState.open} onClose={closeModal}>
        <span
          className="nav-arrow"
          onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
        >
          &#10094;
        </span>
        <img src={modalState.images[modalState.currentIndex]} alt="3 BHK Flat at Gaur Leisure Park Greater Noida West with Modern Interiors" />
        <span
          className="nav-arrow"
          onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
        >
          &#10095;
        </span>
      </Modal>
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <img src={selectedImage} alt="3 BHK Flat at Gaur Leisure Park Greater Noida West with Modern Interiors" />
        </div>
      )}
      {pdfModal.show && (
        <div className="pdf-modal">
          <div className="pdf-modal-content">
            <span
              className="close-pdf-modal"
              onClick={() => setPdfModal({ show: false, type: null, submitted: false })}
            >
              &times;
            </span>

            {pdfModal.submitted ? (
              <div className="download-ready">
                <h3 style={{ color: '#5D4037', marginBottom: '1em' }}>
                  Download Ready!
                </h3>
                <p style={{ marginBottom: '1.5em' }}>
                  Thank you for your interest. Your download will start shortly.
                </p>
                <button
                  className="btn"
                  onClick={handlePdfDownload}
                >
                  Download Now
                </button>
              </div>
            ) : (
              <div className="download-form">
                <h3 style={{ color: '#5D4037', marginBottom: '1em' }}>
                  Get Access to {PDF_TYPES[pdfModal.type].title}
                </h3>
                <h4>
                  Or Call us at <a href="tel:+919211310599" style={{ textDecoration: 'none', color: 'inherit' }}>+91-9211310599</a>
                </h4>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  setPdfModal(prev => ({ ...prev, submitted: true }));
                  handleFormSubmit(e);
                }}>
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    required
                    style={{ width: '100%', padding: '0.8em', marginBottom: '1em', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    required
                    style={{ width: '100%', padding: '0.8em', marginBottom: '1em', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    required
                    style={{ width: '100%', padding: '0.8em', marginBottom: '1.5em', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <button
                    type="submit"
                    className="btn"
                    style={{ width: '100%' }}
                  >
                    Submit & Download
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {contactPopup.show && (
        <div className="contact-popup">
          <span
            className="close-popup"
            onClick={() => setContactPopup({ show: false, card: null })}
          >
            &times;
          </span>
          <h3>Enquire About This Property</h3>
          <h4>
            Or Call us at <a href="tel:+919211310599" style={{ textDecoration: 'none', color: 'inherit' }}>+91-9211310599</a>
          </h4>


          <ContactForm
            onSubmit={handleEnquirySubmit}

            buttonText="Submit & View Details"
          />
        </div>
      )}
    </div>
  );
}

export default App;